<template>
    <!-- SelectedBox -->
    <transition-group name="fade" tag="ul" class="list-group list-tricks">
            <li
                v-for="(trick, index) in selectedListTricks"
                :key="index"
                :draggable="isEditingCombo && reorderMode"
                @dragstart="onDragStart(index, $event)"
                @dragover.prevent="onDragOver(index, $event)"
                @drop.prevent="onDrop(index)"
                :class="{ 'reorder-active': reorderMode }"
            >
                <div
                    class="container position-relative"
                    :ref="`draggableContainer-${index}`"
                    @mousedown="startDragging(index, $event)"
                    @mousemove="handleDragging($event)"
                    @mouseup="stopDragging(index)"
                    @touchstart="startDragging(index, $event)"
                    @touchmove="handleDragging($event)"
                    @touchend="stopDragging(index)"
                >
                    <div
                        class="row position-relative rounded-pill bg-white"
                        :style="{ transform: `translateX(${index === draggingIndex ? containerOffsetX : 0}px)` }"
                        style="z-index: 2; width: auto;"
                    >
                        <div class="col-1 bg-primary d-flex align-items-center bg-secondary bg-gradient bg-opacity-25 rounded-start-pill ms-0 me-0">
                            <div class="level-circle" :class="getBackgroundColorClass(trick.level)">
                                <b class="text-white text-sm-general">{{ trick.level }}</b>
                            </div>
                        </div>
                        <div class="col-8 bg-secondary bg-gradient bg-opacity-25 rounded-0 ms-0 me-0">
                            <div class="row">
                                <div class="col-12 text-start p-1 pb-0 mb-0 text-truncate">
                                    <p class="mb-0 text-sm-title" >
                                        <strong>{{ trick.name }}</strong>
                                    </p>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12 text-start p-1 pt-0 mt-0">
                                    <p class="m-0 p-0 text-secondary text-sm-general">{{ trick.short_name ? trick.short_name : '&nbsp;' }}</p>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-6 p-0">
                                    <div class="rounded-pill border border-secondary-subtle text-center" style="background-color: rgb(37,102,199,0.3)">
                                        <p class="m-0 px-1 text-sm-alerts"><em>{{ getCategoryName(trick.category) }}</em></p>
                                    </div>
                                </div>
                                <div class="col-6 p-0">
                                    <div v-if="trick.grab" class="rounded-pill border border-secondary-subtle text-center" style="background-color: rgba(250,230,41,0.3)">
                                        <p class="m-0 px-1 text-sm-alerts" ><em>{{ getGrabName(trick.grab) }}</em></p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-1 p-0 bg-secondary bg-gradient bg-opacity-25 rounded-0  ms-0 me-0">
                            <div class="flex-column h-100">
                                <div v-if="trick.variation" class="alert alert-secondary m-0 p-0 h-50">
                                    <p class="m-0 text-sm-title">
                                        <strong class="position-absolute top-0 start-50 translate-middle-x" style="line-height: 1.6rem">
                                            {{ trick.variation === 1 ? 'FS' : (trick.variation === 2 ? 'BS' : '') }}
                                        </strong>
                                    </p>
                                </div>
                                <div v-if="trick.dub" class="alert alert-danger m-0 p-0 h-50" style="background-color: rgba(208,0,0,0.7)">
                                    <p class="m-0 text-sm-title">
                                        <strong class="text-white position-absolute top-0 start-50 translate-middle-x">
                                            {{ trick.dub === 1 ? '20%' : (trick.dub === 2 ? '50%' : (trick.dub === 3 ? '80%' : '0%')) }}
                                        </strong>
                                        <i class="fa-solid fa-angle-down text-white position-absolute bottom-0 start-50 translate-middle-x"></i>
                                    </p>
                                </div>

                            </div>
                        </div>
                        <div class="col-2 d-flex justify-content-center align-items-center bg-success bg-gradient bg-opacity-50 rounded-end-pill ms-0 me-0">
                            <div class="text-center text-sm-score">
                                <strong class="p-1">{{ parseFloat(trick.score.toFixed(2)) }}</strong>
                            </div>
                        </div>
                    </div>
                    <!-- Segundo row con botones -->
                    <div
                        class="row position-absolute top-0"
                        style="width: 100%; height: 100%;"
                        :class="{ 'overlay-disabled': reorderMode }"
                    >
                        <div class="col-6 d-flex align-items-center justify-content-start bg-primary bg-opacity-50 rounded-start-pill ms-0 me-0">
                            <i class="fas fa-pencil-alt text-white"></i>
                            <span class="text-white mx-2">
                                <em>
                                    {{ $t('judging_program.selected_tricks_box.edit') }}
                                </em>
                        </span>
                        </div>
                        <div class="col-6 d-flex align-items-center justify-content-end bg-danger bg-opacity-50 rounded-end-pill ms-0 me-0">
                            <span class="text-white mx-2"><em> {{ $t('judging_program.selected_tricks_box.delete') }}</em></span><i class="fas fa-trash text-white"></i>
                        </div>
                    </div>
                </div>
            </li>
    </transition-group>
    <!-- SelectedBox BUTTONS -->
    <div class="btn-group mt-2" role="group" aria-label="List Trick">
        <button
            v-if="isEditingCombo"
            type="button"
            class="btn btn-danger"
            :class="{'disabled-btn': isValidateTrickButtonDisabled }"
            @click.stop="closeToEdit"
            :disabled="isValidateTrickButtonDisabled">
            <span :class="{ 'disabled-icon': isValidateTrickButtonDisabled }" class="check-icon">
                {{ $t('judging_program.selected_tricks_box.cancel_edit') }}
                <i v-if="isValidateTrickButtonDisabled" class="fas fa-ban prohibited-icon"></i>
            </span>
        </button>
        <button
            v-else
            type="button"
            class="btn btn-success"
            :class="{'disabled-btn': isValidateTrickButtonDisabled }"
            @click.stop="addToValidatedTricks"
            :disabled="isValidateTrickButtonDisabled">
            <span :class="{ 'disabled-icon': isValidateTrickButtonDisabled }" class="check-icon">
                {{ $t('judging_program.selected_tricks_box.validate_trick') }}
                <i v-if="isValidateTrickButtonDisabled" class="fas fa-ban prohibited-icon"></i>
            </span>
        </button>
        <div class="separator"></div>
        <button
            v-if="isEditingCombo"
            type="button"
            class="btn btn-warning"
            :class="{'disabled-btn': isValidateComboButtonDisabled }"
            @click.stop="editValidatedCombos(comboValidatedEditIndex)"
            :disabled="isValidateComboButtonDisabled"
            title="" >
            <span :class="{ 'disabled-icon': isValidateComboButtonDisabled }" class="check-icon">
                {{ $t('judging_program.selected_tricks_box.validate_edit_combo') }}
                <i v-if="isValidateComboButtonDisabled" class="fas fa-ban prohibited-icon"></i>
            </span>
        </button>
        <button
            v-else
            type="button"
            class="btn btn-primary"
            :class="{'disabled-btn': isValidateComboButtonDisabled }"
            @click.stop="addValidatedCombos"
            :disabled="isValidateComboButtonDisabled">
            <span :class="{ 'disabled-icon': isValidateComboButtonDisabled }" class="check-icon">
                {{ $t('judging_program.selected_tricks_box.validate_combo') }}
                <i v-if="isValidateComboButtonDisabled" class="fas fa-ban prohibited-icon"></i>
            </span>
        </button>
    </div>
</template>

<script>
export default {
    name: "SelectedBox",
    emits: [
        'update-validated-tricks',
        'clear-selected-tricks',
        'update-tricks-selected',
        'delete-trick-selected',
        'reorder-selected-tricks',
        'reorder-mode-changed',
    ],
    data() {
        return {
            categories : [
                { id: 1, name: 'Aerial'},
                { id: 2, name: 'Aerial / Flip'},
                { id: 3, name: 'Aerial / Switch'},
                { id: 4, name: 'Dynamic'},
                { id: 5, name: 'Dynamic / Flip'},
                { id: 6, name: 'Dynamic / Flip / Switch '},
                { id: 7, name: 'Dynamic / Switch'},
                { id: 8, name: 'Dynamic / Transition'},
                { id: 9, name: 'Mount'},
                { id: 10, name: 'Static'},
                { id: 11, name: 'Transition'},
            ],
            grabs : [
                {id: 1, name: 'Blunt', value: '1'},
                {id: 2, name: 'Boneless', value: '2'},
                {id: 3, name: 'BuddhaGrab', value: '3'},
                {id: 4, name: 'CanOpener', value: '4'},
                {id: 5, name: 'ChristAir', value: '5'},
                {id: 6, name: 'Crossover', value: '6'},
                {id: 7, name: 'CrossoverSafety', value: '7'},
                {id: 8, name: 'CrossoverTruckDriver', value: '8'},
                {id: 9, name: 'Flat', value: '9'},
                {id: 10, name: 'HalfSquirrelLef.', value: '10'},
                {id: 11, name: 'HalfSquirrelRig.', value: '11'},
                {id: 12, name: 'HighChair', value: '12'},
                {id: 13, name: 'HorizontalTwist', value: '13'},
                {id: 14, name: 'Japan', value: '14'},
                {id: 15, name: 'KneelAir', value: '15'},
                {id: 16, name: 'Layout', value: '16'},
                {id: 17, name: 'Mute', value: '17'},
                {id: 18, name: 'No grab', value: '18'},
                {id: 19, name: 'Open', value: '19'},
                {id: 20, name: 'Pike', value: '20'},
                {id: 21, name: 'SafetyGrab', value: '21'},
                {id: 22, name: 'Scratch', value: '22'},
                {id: 23, name: 'Seatbelt', value: '23'},
                {id: 24, name: 'Shifty', value: '24'},
                {id: 25, name: 'SpreadEagle', value: '25'},
                {id: 26, name: 'SquirrelGrab', value: '26'},
                {id: 27, name: 'Superman', value: '27'},
                {id: 28, name: 'TruckDriver', value: '28'},
                {id: 29, name: 'Tuck', value: '29'},
                {id: 30, name: 'TuckBlunt', value: '30'},
                {id: 31, name: 'TuckedTwist', value: '31'},
                {id: 32, name: 'Twist', value: '32'},
                {id: 33, name: 'Yokohama', value: '33'},
            ],
            draggingIndex: null,
            isDragging: false,
            startX: 0,
            containerOffsetX: 0,
            reorderMode: false,
            dragSourceIndex: null,
            dragThreshold: 80,
            maxDragOffset: 140,
        };
    },
    props: {
        selectedListTricks: Array,
        validatedTricks: Array,
        comboCount: Number,
        comboValidatedEditIndex: Number,
    },
    computed: {
        isEditingCombo() {
            return this.comboValidatedEditIndex !== null && this.comboValidatedEditIndex !== undefined && this.comboValidatedEditIndex >= 0;
        },
        isValidateTrickButtonDisabled() {
            return this.selectedListTricks.length < 1;
        },
        isValidateComboButtonDisabled() {
            const lastTrick = this.selectedListTricks[this.selectedListTricks.length - 1];
            return this.selectedListTricks.length <= 1 || (lastTrick && lastTrick.dub === 3) || this.reorderMode;
        },
    },
    watch: {
        comboValidatedEditIndex(newVal) {
            // Ensure we leave reorder mode when the user exits combo edit
            const editing = newVal !== null && newVal !== undefined && newVal >= 0;
            if (!editing && this.reorderMode) {
                this.reorderMode = false;
                this.$emit('reorder-mode-changed', false);
            }
        },
    },
    methods: {
        /*SelectedBox*/
        getBackgroundColorClass(level) {
            switch (level) {
                case 1:
                    return 'color-level-1';
                case 2:
                    return 'color-level-2';
                case 3:
                    return 'color-level-3';
                case 4:
                    return 'color-level-4';
                default:
                    return '';
            }
        },
        getCategoryName(id) {
            const category = this.categories.find((c) => c.id === id);
            return category ? category.name : '';
        },
        getGrabName(id) {
            const grab = this.grabs.find((c) => c.id === id);
            return grab ? grab.name : '';
        },
       /* SelectedBox BUTTONS*/
        addToValidatedTricks() {
            if (this.selectedListTricks !== null) {
                this.selectedListTricks.forEach((trick) => {
                    const newSelectedTrick = {
                        id: trick.id,
                        name: trick.name,
                        short_name: trick.short_name,
                        category: trick.category,
                        type: trick.type,
                        level: trick.level,
                        creator: trick.creator,
                        value: trick.value,
                        variation: trick.variation,
                        grab: trick.grab,
                        dub: trick.dub,
                        score: trick.value
                    };
                    this.$emit('update-validated-tricks',null,null, newSelectedTrick);
                });
                this.$emit('clear-selected-tricks');
            }else{
                console.error('ERROR: Can´t validated this tricks.');
            }
        },
        addValidatedCombos() {
            if (this.selectedListTricks !== null) {
                const comboData = {
                    header: {
                        combo_id: this.comboCount,
                        total_tricks_combo: 0,
                        total_score: 0,
                    },
                    body: [],
                };
                this.selectedListTricks.forEach((trick) => {
                    const newSelectedTrick = {
                        id: trick.id,
                        combo_id: this.comboCount,
                        name: trick.name,
                        short_name: trick.short_name,
                        category: trick.category,
                        type: trick.type,
                        level: trick.level,
                        creator: trick.creator,
                        value: trick.value,
                        variation: trick.variation,
                        grab: trick.grab,
                        dub: trick.dub,
                        score: trick.value,
                    };
                    comboData.header.total_score += trick.value;
                    comboData.body.push(newSelectedTrick);
                });
                comboData.header.total_tricks_combo = comboData.body.length;
                this.$emit('update-validated-tricks', null,null,comboData);
                this.$emit('clear-selected-tricks');
            } else {
                console.error('ERROR: Can´t validated this combo.');
            }
        },
        editValidatedCombos(comboValidatedEditIndex) {
            if (this.selectedListTricks !== null) {
                const combosInList = this.validatedTricks[comboValidatedEditIndex];
                const comboData = {
                    header: {
                        combo_id: combosInList.header.combo_id,
                        total_tricks_combo: 0,
                        total_score: 0,
                    },
                    body: [],
                };
                this.selectedListTricks.forEach((trick) => {
                    const newSelectedTrick = {
                        id: trick.id,
                        combo_id: combosInList.header.combo_id,
                        name: trick.name,
                        short_name: trick.short_name,
                        category: trick.category,
                        type: trick.type,
                        level: trick.level,
                        creator: trick.creator,
                        value: trick.value,
                        variation: trick.variation,
                        grab: trick.grab,
                        dub: trick.dub,
                        score: trick.value,
                    };
                    comboData.header.total_score += trick.value;
                    comboData.body.push(newSelectedTrick);
                });
                comboData.header.total_tricks_combo = comboData.body.length;
                this.$emit('update-validated-tricks', comboValidatedEditIndex,null,comboData);
                this.$emit('clear-selected-tricks');
                this.reorderMode = false;
                this.$emit('reorder-mode-changed', false);

            } else {
                console.error('ERROR: Can´t validated this combo.');
            }
        },
        closeToEdit(){
            this.$emit('clear-selected-tricks');
            this.reorderMode = false;
            this.$emit('reorder-mode-changed', false);
        },
        startDragging(index, event) {
            if (this.reorderMode) {
                return;
            }
            this.isDragging = true;
            this.startX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX;
            this.containerOffsetX = 0;
            this.draggingIndex = index;
            },
        handleDragging(event) {
            if (this.reorderMode) {
                return;
            }
            if (this.isDragging && event) {
                const currentX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX;
                const delta = currentX - this.startX;
                // Clamp to avoid infinite slide off the card
                const clamped = Math.max(-this.maxDragOffset, Math.min(this.maxDragOffset, delta));
                this.containerOffsetX = clamped;
            }
        },
        stopDragging(index) {
            if (this.reorderMode) {
                return;
            }
            if (this.isDragging) {
                const threshold = this.dragThreshold;
                if (Math.abs(this.containerOffsetX) > threshold) {
                    this.containerOffsetX > 0 ? this.editItem(index) : this.deleteItem(index);
                }
                this.isDragging = false;
                this.startX = 0;
                this.containerOffsetX = 0;
                this.draggingIndex  = null;
            }
        },
        editItem(index) {
            this.$emit("update-tricks-selected", index);
        },
        deleteItem(index) {
            this.$emit("delete-trick-selected", index);
        },
        toggleReorderMode() {
            this.reorderMode = !this.reorderMode;
        },
        toggleReorderModeFromParent() {
            if (!this.isEditingCombo) return;
            this.reorderMode = !this.reorderMode;
            this.$emit('reorder-mode-changed', this.reorderMode);
        },
        onDragStart(index, event) {
            if (!this.reorderMode || !this.isEditingCombo) return;
            this.dragSourceIndex = index;
            if (event && event.dataTransfer) {
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', String(index));
            }
        },
        onDragOver(index, event) {
            if (!this.reorderMode || !this.isEditingCombo) return;
            if (event && event.preventDefault) {
                event.preventDefault();
            }
        },
        onDrop(index) {
            if (!this.reorderMode || !this.isEditingCombo) return;
            if (this.dragSourceIndex === null || this.dragSourceIndex === index) {
                this.dragSourceIndex = null;
                return;
            }
            const newOrder = [...this.selectedListTricks];
            const [item] = newOrder.splice(this.dragSourceIndex, 1);
            newOrder.splice(index, 0, item);
            this.dragSourceIndex = null;
            this.$emit('reorder-selected-tricks', newOrder);
        },

    }
};
</script>

<style scoped>
/*SelectedBox*/
.list-tricks {
    list-style-type: none;
    padding: 0;
    max-height: 300px;
    overflow-y: auto;
    overflow-x: hidden;

}
.list-tricks li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #ccc;
    padding: 8px;
}
.level-circle {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    border-radius: 50%;
    border: 1px solid #000;
    line-height: 7px;
}
.text-sm-general{
    font-size: 0.6rem;
    line-height: 10px;
}
.text-sm-alerts{
    font-size: 0.6rem;
}
.text-sm-score{
    font-size: 0.6rem;
    width: 17vw;
}
.text-sm-title{
    font-size: 0.7rem;
}
.reorder-active {
    cursor: grab;
}
.overlay-disabled {
    pointer-events: none;
}
.with-borders {
    border-top: 1px solid #000000;
    border-bottom: 1px solid #000000;
    border-right: 2px solid #000000;
    border-left: 2px solid #000000;
    text-align: center;
}
.color-level-1 {
    color:#ffffff;
    background-color: rgb(8,76,255);
}
.color-level-2 {
    color:#ffffff;
    background-color: rgb(23,231,0);
}
.color-level-3 {
    color:#ffffff;
    background-color: rgb(255,126,0);
}
.color-level-4 {
    color:#ffffff;
    background-color: rgb(255,0,0);
}
.disabled-btn {
    opacity: 0.7;
    cursor: not-allowed;
}
.disabled-icon {
    color: #c0c0c0;
    transform: scale(0.9);
    transition: transform 0.2s ease;
}
.prohibited-icon {
    color: red;
    float: right;
}
/* SelectedBox BUTTONS*/
.separator {
    width: 10px; /* Ajusta el ancho de la separación según sea necesario */
}


@media (min-width: 450px) {
    .text-sm-general{
        font-size: 1rem;
        line-height: 25px;
    }
    .text-sm-alerts{
        font-size: 0.9rem;
    }
    .text-sm-score{
        font-size: 1rem;
        width: 10vw;
    }
    .text-sm-title{
        font-size: 1rem;
    }
    .level-circle {
        width: 30px;
        height: 30px;
        line-height: 25px;

    }
}
</style>